'use client';

import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { isEmpty } from 'lodash';
import { usePathname } from 'next/navigation';

import { useAuthContext } from '@/components/Providers/AuthProvider';
import { RECOMMENDED_ITEMS_KEY } from '@/config/constants';
import { useGetRecommendedItemsQuery } from '@/store/api/customerAgainstWebAlias';
import { RecommendedItemDTO } from '@/types/Customer';

interface AppContextProps {
  isBackOffice: boolean;
  isEzShipSidebarVisible: boolean;
  setVisibilityOfEzShipSidebar: (isVisible: boolean) => void;
  recommendedForItems?: RecommendedItemDTO[];
}

interface AppProviderProps {
  children: ReactNode;
  isBackOffice?: boolean;
}
const AppContext = createContext<AppContextProps>({
  isBackOffice: false,
  isEzShipSidebarVisible: false,
  recommendedForItems: undefined,
} as AppContextProps);

const storage = typeof globalThis !== 'undefined' && globalThis.localStorage ? globalThis.localStorage : null;
const recommendedItemsInStorage = storage?.getItem(RECOMMENDED_ITEMS_KEY);

const AppProvider = ({ isBackOffice = false, children }: AppProviderProps) => {
  const storage = typeof globalThis !== 'undefined' && globalThis.localStorage ? globalThis.localStorage : null;
  const recommendedItemsInStorage = storage?.getItem(RECOMMENDED_ITEMS_KEY);

  const appState = useMemo(() => ({ isBackOffice }), [isBackOffice]);

  const [isEzShipSidebarVisible, setVisibilityOfEzShipSidebar] = useState(false);

  const pathname = usePathname();
  const { isLoggedIn } = useAuthContext();

  const [recommendedForData, setRecommendedForData] = useState(() => {
    return isLoggedIn && recommendedItemsInStorage ? recommendedItemsInStorage : '';
  });

  const hasPetProfilesRecommendedItems = useMemo(() => !!recommendedItemsInStorage, [recommendedItemsInStorage]);

  const { data: recommendedItems, isLoading: isLoadingRecommendedItems } = useGetRecommendedItemsQuery(undefined, {
    skip: hasPetProfilesRecommendedItems || !isLoggedIn,
  });

  useEffect(() => {
    if (!isLoggedIn) {
      storage?.removeItem(RECOMMENDED_ITEMS_KEY);
    } else if (!isEmpty(recommendedItems) && !pathname.includes('logout')) {
      storage?.setItem(RECOMMENDED_ITEMS_KEY, JSON.stringify(recommendedItems));
    }
  }, [isLoggedIn, recommendedItems, pathname, storage]);

  useEffect(() => {
    if (isLoggedIn && recommendedItemsInStorage) {
      setRecommendedForData(() => recommendedItemsInStorage);
    }
  }, [isLoggedIn, recommendedItems, pathname, recommendedItemsInStorage]);

  const handleStorageChange = useCallback((e: StorageEvent) => {
    if (e.key === RECOMMENDED_ITEMS_KEY) setRecommendedForData(() => e.newValue ?? '');
  }, []);

  useEffect(() => {
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [handleStorageChange]);

  const recommendedFor = useMemo(() => {
    if (!isEmpty(recommendedForData) && recommendedForData !== 'undefined' && recommendedForData) {
      try {
        const parsedData = JSON.parse(recommendedForData);

        if (!isEmpty(parsedData)) {
          return parsedData as RecommendedItemDTO[];
        }
      } catch (error) {
        console.error('Error parsing JSON:', error);
      }
    }
    return undefined;
  }, [recommendedForData]);

  const providedValue = useMemo(
    () => ({
      ...appState,
      isEzShipSidebarVisible,
      setVisibilityOfEzShipSidebar,
      recommendedForItems: recommendedFor,
    }),
    [appState, isEzShipSidebarVisible, setVisibilityOfEzShipSidebar, recommendedFor]
  );

  return <AppContext.Provider value={providedValue}>{children}</AppContext.Provider>;
};

export const useAppContext = () => {
  return useContext(AppContext);
};

export default AppProvider;
